/**
 * Draft Variant Selector
 * Shows 4 tone variants (direct, warm, strategic, short) and lets user pick one.
 */

import React, { useState } from 'react';
import { env } from '@/lib/env';
import { getAuthItem } from '@/lib/authStorage';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { emailApiBase } from '@/lib/email-api'

export interface DraftVariant {
  tone: string;
  subject: string;
  body: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  variants: DraftVariant[];
  loading: boolean;
  onSelectVariant: (variant: DraftVariant) => void;
  contactEmail?: string;
}

const TONE_LABELS: Record<string, string> = {
  direct: 'Direct',
  warm: 'Warm',
  strategic: 'Strategic',
  short: 'Short',
};

const TONE_DESCRIPTIONS: Record<string, string> = {
  direct: 'Clear ask, no fluff',
  warm: 'Relationship-first, softer',
  strategic: 'Positions value, timing',
  short: '1-2 sentences, casual',
};

export function DraftVariantSelector({ isOpen, onClose, variants, loading, onSelectVariant, contactEmail }: Props) {
  const [selectedTone, setSelectedTone] = useState<string>(variants[0]?.tone || 'direct');
  const [editedBodies, setEditedBodies] = useState<Record<string, string>>({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  if (!isOpen) return null;

  const selectedVariant = variants.find(v => v.tone === selectedTone) || variants[0];
  const displayBody = editedBodies[selectedTone] ?? selectedVariant?.body ?? '';

  const handleSelectVariant = () => {
    if (!selectedVariant) return;
    onSelectVariant({
      ...selectedVariant,
      body: editedBodies[selectedTone] ?? selectedVariant.body,
    });
  };

  const handleSaveAsGmailDraft = async () => {
    if (!selectedVariant || !contactEmail) return;
    setSavingDraft(true);
    try {
      const token = typeof window !== 'undefined' ? getAuthItem('xavior_auth_token') : null;
      const userId = typeof window !== 'undefined' ? getAuthItem('userId') : null;
      const res = await fetch(`${emailApiBase()}/drafts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'X-User-Id': userId } : {}),
        },
        body: JSON.stringify({
          to: contactEmail,
          subject: selectedVariant.subject,
          body: editedBodies[selectedTone] ?? selectedVariant.body,
        }),
      });
      if (res.ok) {
        setDraftSaved(true);
        setTimeout(() => onClose(), 1200);
      }
    } catch {
      // Best-effort
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <span style={{ fontWeight: 600, fontSize: '15px' }}>Draft Variants</span>
          <button onClick={onClose} style={closeButtonStyle}>&times;</button>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted, #999)' }}>
            <div style={{ fontSize: '14px', marginBottom: '8px' }}>Generating drafts...</div>
            <div style={{ fontSize: '12px' }}>AI is crafting 4 tone variants based on your relationship context.</div>
          </div>
        ) : variants.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted, #999)' }}>
            No variants generated. Try again.
          </div>
        ) : (
          <>
            {/* Tone Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border-light, #eee)', padding: '0 16px' }}>
              {variants.map(v => (
                <button
                  key={v.tone}
                  onClick={() => setSelectedTone(v.tone)}
                  style={{
                    padding: '10px 14px',
                    fontSize: '13px',
                    fontWeight: selectedTone === v.tone ? 600 : 400,
                    color: selectedTone === v.tone ? 'var(--accent, #1a73e8)' : 'var(--text-secondary, #666)',
                    background: 'none',
                    border: 'none',
                    borderBottom: selectedTone === v.tone ? '2px solid var(--accent, #1a73e8)' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {TONE_LABELS[v.tone] || v.tone}
                </button>
              ))}
            </div>

            {/* Content */}
            {selectedVariant && (
              <div style={{ padding: '16px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted, #999)', marginBottom: '12px' }}>
                  {TONE_DESCRIPTIONS[selectedTone] || ''}
                </div>

                {/* Subject */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Subject</label>
                  <div style={{
                    fontSize: '14px',
                    padding: '8px 10px',
                    background: 'var(--bg-secondary, #f9fafb)',
                    borderRadius: '6px',
                    border: '1px solid var(--border-light, #eee)',
                  }}>
                    {selectedVariant.subject}
                  </div>
                </div>

                {/* Body */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Body</label>
                  <textarea
                    value={displayBody}
                    onChange={(e) => setEditedBodies(prev => ({ ...prev, [selectedTone]: e.target.value }))}
                    style={{
                      width: '100%',
                      minHeight: '140px',
                      fontSize: '13px',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-light, #ddd)',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: 1.5,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {draftSaved && (
                  <div style={{ color: '#16a34a', fontSize: '13px', fontWeight: 500, marginBottom: '8px', textAlign: 'right' }}>
                    Draft saved to Gmail!
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button onClick={onClose} style={secondaryButtonStyle}>Cancel</button>
                  {contactEmail && (
                    <button
                      onClick={handleSaveAsGmailDraft}
                      disabled={savingDraft || draftSaved}
                      style={draftButtonStyle}
                    >
                      {savingDraft ? 'Saving...' : draftSaved ? 'Saved!' : 'Save as Draft'}
                    </button>
                  )}
                  <button onClick={handleSelectVariant} style={primaryButtonStyle}>Compose & Send</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ==================== STYLES ====================

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10001,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-primary, white)',
  borderRadius: '12px',
  width: '560px',
  maxWidth: '90vw',
  maxHeight: '80vh',
  overflow: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 16px',
  borderBottom: '1px solid var(--border-light, #eee)',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  color: 'var(--text-muted, #999)',
  padding: '0 4px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--text-secondary, #666)',
  display: 'block',
  marginBottom: '4px',
};

const primaryButtonStyle: React.CSSProperties = {
  fontSize: '13px',
  padding: '8px 16px',
  borderRadius: '6px',
  border: 'none',
  background: 'var(--accent, #1a73e8)',
  color: 'white',
  cursor: 'pointer',
  fontWeight: 600,
};

const draftButtonStyle: React.CSSProperties = {
  fontSize: '13px',
  padding: '8px 16px',
  borderRadius: '6px',
  border: '1px solid #16a34a',
  background: 'var(--bg-primary, white)',
  cursor: 'pointer',
  color: '#16a34a',
  fontWeight: 600,
};

const secondaryButtonStyle: React.CSSProperties = {
  fontSize: '13px',
  padding: '8px 16px',
  borderRadius: '6px',
  border: '1px solid var(--border-light, #ddd)',
  background: 'var(--bg-primary, white)',
  cursor: 'pointer',
  color: 'var(--text-secondary, #666)',
};
